import React, { useContext, useEffect, useMemo, useState } from 'react'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import { successToast, ToastContext } from '@/components/Toast/Toast'
import { CompanyTag } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { ViewTabs } from '@/components/ui/tabs'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import { BonusTransactionDto } from 'api/Transaction/Types/BonusTransactionDto'
import { RawTransactionFilterKey } from 'api/Transaction/Types/RawTransactionDto'
import { TransactionDto } from 'api/Transaction/Types/Transaction'
import { TransactionsStatusReason } from 'api/Transaction/Types/TransactionsStatusReason'
import { isIBCommission } from 'api/Wallet/Types/TransactionTypeDto'
import { WalletDto } from 'api/Wallet/Types/Wallet'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { WarningOutlined } from 'components/Icon'
import { useAppDispatch, useAppSelector } from 'store'
import { initialState } from 'store/grid'
import {
  fetchTransactionReasonStatusAction,
  fetchTransactionStatusAction,
} from 'store/transactions/transaction.action'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, FilterLogic } from 'utils/QueryHelpers'
import { sleep } from 'utils/sleep'
import { isNotAllowedToChangeState } from 'utils/transaction/status.index'
import { getClientWalletNonPaymentAgentType } from 'utils/wallet'

import ClientAreaAuditLogModal from '../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { AggregateDataTable } from '../AggregateDataTable'
import { TransactionDetailsTab } from '../DetailTabs'
import { LinkedTransactionTable } from '../LinkedTransactions'
import { PaymentProviderTransactionTable } from '../PaymentProviderTransactionTable'
import { TransactionDetailsView } from '../ResolveTransactionDetailsRoute'
import TransactionDetailsDealsPage from '../TransactionDetailsDeals/TransactionDetailsDealsPage'
import { TransactionDetailsLotsProfitStatistics } from '../TransactionDetailsLotsProfitStatistics/TransactionDetailsLotsProfitStatistics'
import TransactionDetailsNotesPage from '../TransactionDetailsNotes/TransactionDetailsNotesPage'
import { TransactionClientBonuses } from '../TransactionDetailsWalletDepositWithdrawal/TransactionClientBonuses'
import { TransactionClientWallets } from '../TransactionDetailsWalletDepositWithdrawal/TransactionClientWallets'
import TransactionHistoryDetails from '../TransactionHistoryDetails'
import { TransactionsStatisticOverview } from '../TransactionsStatisticOverview/TransactionsStatisticOverview'
import { TransactionStatusHeaderAction } from '../TransactionStatusHeaderAction'
import { TransactionDetailsAdjustmentWalletAndPaymentAgentGeneralPage } from './TransactionDetailsAdjustmentWalletAndPaymentAgentGeneralPage'

interface Props {
  data: TransactionDto
  forceRefresh(): void
}

const TransactionDetailsAdjustmentWalletAndPaymentAgentPage: React.FC<Props> = ({
  data,
  forceRefresh,
}) => {
  const dispatch = useAppDispatch()

  const [auth] = useContext(AuthSessionContext)
  const walletsApiClient = useApiClient(WalletsApiClient)
  const setToast = useContext(ToastContext)

  const [confirmationModal, setConfirmationModal] = useState<{
    visible: boolean
    transaction?: TransactionDto
    statusId?: number
    statusReason?: TransactionsStatusReason
  }>({ visible: false })

  const transactionState = useAppSelector((state) => state.transactions.status)
  const statusReasons = useAppSelector((state) => state.transactions.statusReasons)

  const permissions = auth?.permissions || []
  const [client, setClient] = useState<{ id: string; name: string } | undefined>(undefined)
  const [transactionWalletId, setTransactionWalletId] = useState<string | undefined>()

  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false)
  const [tempReason, setTempReason] = useState<TransactionsStatusReason | null>(null)
  const [reasonInput, setReasonInput] = useState<string | null>(null)
  const [auditLogVisible, setAuditLogVisible] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<string>(TransactionDetailsTab.GENERAL)
  const [wallets, setWallets] = useState<WalletDto[]>([])
  const [bonuses, setBonuses] = useState<PageData<BonusTransactionDto[]> | undefined>(undefined)

  useEffect(() => {
    if (data) {
      let transactionWallet
      if (data.type.id === TransactionDetailsView.WithdrawToPaymentAgent) {
        transactionWallet = getClientWalletNonPaymentAgentType(
          data.walletTransactions,
          'Debit'
        )?.wallet
      } else {
        transactionWallet = getClientWalletNonPaymentAgentType(data.walletTransactions)?.wallet
      }
      const clientFromTA = data?.tradingAccountTransactions?.find((x) => x.client)?.client
      if (transactionWallet) {
        setClient(transactionWallet.client)
        setTransactionWalletId(transactionWallet.id)
      } else if (clientFromTA) setClient(clientFromTA)

      if (isEmpty(statusReasons)) dispatch(fetchTransactionReasonStatusAction(walletsApiClient))
      if (isEmpty(transactionState)) dispatch(fetchTransactionStatusAction(walletsApiClient))
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateTransactionStatus = async (
    statusId: number,
    statusReason: TransactionsStatusReason | null
  ) => {
    if (data) {
      setConfirmationModal({
        visible: true,
        transaction: data,
        statusId,
        statusReason: statusReason || undefined,
      })
    }
  }

  const handleChangeStatus = async () => {
    const { statusReason, statusId, transaction } = confirmationModal
    if (!statusReason && statusId && transaction) {
      await walletsApiClient.updateTransactionStatus(statusId, { transactionId: transaction.id })
      setToast(successToast('Transaction status updated.'))
    }
    if (statusReason && transaction) {
      if (statusReason.description === 'Other') {
        setTempReason(statusReason)
        setConfirmationModal({ visible: false })
        return setReasonInput(' ')
      } else {
        await walletsApiClient.updateTransactionStatus(statusReason.transactionState.id, {
          transactionId: transaction.id,
          reason: null,
          transactionStateReasonId: statusReason.id,
        })
        setToast(successToast('Transaction status updated.'))
      }
    }
    setConfirmationModal({
      visible: false,
      statusId: undefined,
      transaction: undefined,
      statusReason: undefined,
    })
    forceRefresh()
  }

  const getTickmillCompanyIfExist = () => {
    return data?.tradingAccountTransactions[0]?.tickmillCompany
  }

  const renderMenu = () => {
    // if transaction is failed, we want allow to have option to set Completed only
    if (data && data.state.id === 500) {
      const completed = transactionState.find((x) => x.id === 200)
      if (!completed) {
        return null
      }
      return (
        <DropdownMenuItem onSelect={() => updateTransactionStatus(completed.id, null)}>
          Set without reason
        </DropdownMenuItem>
      )
    }

    return (
      <>
        {transactionState.map((x) => (
          <DropdownMenuSub key={x.id}>
            <DropdownMenuSubTrigger
              disabled={
                !hasPermission(permissions, PermissionsEnum.CanEditTransactionStatus) ||
                x.id === 50 ||
                x.id === 100 ||
                x.id === 600
              }
            >
              {x.name}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem onSelect={() => updateTransactionStatus(x.id, null)}>
                Set without reason
              </DropdownMenuItem>
              {statusReasons
                .filter((reason) => reason.transactionState.id === x.id)
                .map((reason) => (
                  <DropdownMenuItem
                    key={reason.id}
                    onSelect={() => updateTransactionStatus(reason.id, reason)}
                    disabled={x.id === 50 || x.id === 100 || x.id === 600}
                  >
                    {reason.description}
                  </DropdownMenuItem>
                ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}
      </>
    )
  }

  const tickmillCompany = getTickmillCompanyIfExist()

  const getWalletsByClient = async (clientId: string) => {
    const result = await walletsApiClient.getWalletsByClientId(clientId)
    setWallets(result)
  }

  useEffect(() => {
    if (client && isEmpty(wallets)) {
      getWalletsByClient(client.id)
    }
    if (client && isEmpty(bonuses)) {
      getActiveBonusesByClient(client.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client])

  const getActiveBonusesByClient = async (clientId: string) => {
    const query = buildQuery(createBonusTransactionsQuery(clientId).search)
    const result = await walletsApiClient.getBonusTransactions({
      search: query,
      pageSize: 100,
      pageIndex: 1,
    })
    setBonuses(result)
  }

  const showBonusesTabWarning = useMemo(() => {
    return !!bonuses?.items?.length
  }, [bonuses])

  const isStatusMenuDisabled =
    !hasPermission(permissions, PermissionsEnum.CanEditTransactionStatus) &&
    isNotAllowedToChangeState(data.state.id)

  return (
    <React.Fragment>
      {reasonInput && tempReason && (
        <Dialog open={!!reasonInput} onOpenChange={() => setReasonInput(null)}>
          <DialogContent className='max-w-md'>
            <DialogHeader>
              <DialogTitle>Reason</DialogTitle>
            </DialogHeader>
            <div className='px-4 pb-4'>
              <Field>
                <FieldLabel>Reason</FieldLabel>
                <Input
                  onChange={(e) => setReasonInput(e.target.value)}
                  type='text'
                  value={reasonInput}
                />
              </Field>
              <div className='flex justify-center pt-4'>
                <Button
                  type='button'
                  onClick={async () => {
                    await walletsApiClient.updateTransactionStatus(
                      tempReason?.transactionState.id,
                      {
                        transactionId: data!.id,
                        reason: reasonInput?.trimLeft(),
                        transactionStateReasonId: tempReason.id,
                      }
                    )
                    setToast(successToast('Transaction status updated.'))
                    await sleep(2000)
                    setReasonInput(null)
                    forceRefresh()
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {confirmationModal.visible && (
        <ConfirmationModal
          contentText={`Updating a transaction's state while that is ${confirmationModal.transaction?.state.name} could result to issues. Are you sure you want to proceed?`}
          yesButtonText='Yes, proceed'
          onYes={handleChangeStatus}
          onCancel={() => setConfirmationModal({ visible: false })}
          visible={!!confirmationModal.visible}
        />
      )}
      {showHistoryModal && (
        <Modal width={400} visible={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
          <TransactionHistoryDetails id={data.id} />
        </Modal>
      )}
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          transactionId={data.id}
        />
      )}
      <NavigationHeader>
        <span className='font-semibold'>{data.referenceId}</span>
        {tickmillCompany && <CompanyTag id={tickmillCompany.id}>{tickmillCompany.name}</CompanyTag>}
        <TransactionStatusHeaderAction
          statusName={data.state.name}
          onStatusClick={() => setShowHistoryModal(true)}
          menuDisabled={isStatusMenuDisabled}
          menuContent={!isNotAllowedToChangeState(data.state.id) ? renderMenu() : undefined}
        />
      </NavigationHeader>
      <ViewTabs
        defaultKey={TransactionDetailsTab.GENERAL}
        activeTab={activeTab}
        onChange={(value) => setActiveTab(value)}
        tabs={[
          {
            key: TransactionDetailsTab.GENERAL,
            translateKey: 'General',
            visible: true,
            component: (
              <TransactionDetailsAdjustmentWalletAndPaymentAgentGeneralPage
                transaction={data}
                client={client}
                permissions={permissions}
                setAuditLogModal={setAuditLogVisible}
                setActiveTab={setActiveTab}
                forceRefresh={forceRefresh}
              />
            ),
          },
          {
            key: TransactionDetailsTab.FUNDING_SUMMARY,
            translateKey: <span>Funding Summary</span>,
            visible: true,
            component: <AggregateDataTable clientId={client?.id} />,
          },
          {
            key: TransactionDetailsTab.FUNDING_DETAIL,
            translateKey: <span>Funding Details</span>,
            visible: true,
            component: <PaymentProviderTransactionTable clientId={client?.id} />,
          },
          {
            key: TransactionDetailsTab.STATISTICS,
            translateKey: <span>Trading Account Statistics</span>,
            visible: true,
            component: client && <TransactionsStatisticOverview client={client} />,
          },
          {
            key: TransactionDetailsTab.LOTS_PROFIT_STATS,
            translateKey: <span>Lots/Profit Statistics</span>,
            visible: true,
            component: (
              <TransactionDetailsLotsProfitStatistics
                clientId={client?.id}
                walletId={transactionWalletId}
              />
            ),
          },
          {
            key: TransactionDetailsTab.BONUS,
            translateKey: (
              <span>
                {!!showBonusesTabWarning && <WarningOutlined style={{ color: 'red' }} />} Bonus
              </span>
            ),
            visible: true,
            component: client && <TransactionClientBonuses client={client} />,
          },
          {
            key: TransactionDetailsTab.WALLETS,
            translateKey: <span> Wallets</span>,
            visible: true,
            component: client && <TransactionClientWallets wallets={wallets} />,
          },
          {
            key: TransactionDetailsTab.LINKS,
            translateKey: (
              <span>
                {(!!data.linkedTo.length || !!data.linkedFrom.length) && (
                  <WarningOutlined style={{ color: 'red' }} />
                )}{' '}
                Links
              </span>
            ),
            visible: true,
            component: <LinkedTransactionTable data={data} />,
          },
          {
            key: TransactionDetailsTab.NOTES,
            translateKey: <span>Notes</span>,
            visible: !isIBCommission(data?.type.id),
            component: <TransactionDetailsNotesPage transaction={data} />,
          },
          {
            key: TransactionDetailsTab.DEALS,
            translateKey: <span>Deals</span>,
            visible: isIBCommission(data?.type.id),
            component: <TransactionDetailsDealsPage transaction={data} />,
          },
        ]}
      />
    </React.Fragment>
  )
}

const createBonusTransactionsQuery = (clientId: string) => {
  const initialGridState = initialState()
  return {
    search: {
      ...initialGridState.filter,
      logic: initialGridState.filter.logic,
      defaultFilters: {
        logic: FilterLogic.AND,
        filters: [
          {
            field: 'Client',
            operator: '==',
            value: clientId,
          },
          {
            field: RawTransactionFilterKey.bonusTransactionStatusId,
            operator: '==',
            value: 10,
          },
        ],
      },
    },
  }
}

export default TransactionDetailsAdjustmentWalletAndPaymentAgentPage
